import { v4 as uuidv4 } from 'uuid';

const STORAGE_KEY = 'pos_payment_trace_number';

/**
 * Generates a new UUID v4 to use as a trace number
 * @returns {string} UUID v4 string
 */
export const generateTraceNumber = () => {
  return uuidv4();
};

/**
 * Retrieves the stored trace state (trace number, payment parameters, and
 * PAUI tombstones) from localStorage.
 * @returns {object|null} Object with { trace_number, lastPaymentParams } or null if not found
 */
export const getTraceStateFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }
    return JSON.parse(stored);
  } catch (error) {
    console.error(
      'Error reading trace state from localStorage:',
      error.message,
    );
    return null;
  }
};

/**
 * Saves trace state (trace number, payment parameters, and any PAUI
 * tombstones) to localStorage.
 * @param {string} traceNumber - The trace number to store
 * @param {object} paymentParams - The payment parameters to store
 */
export const saveTraceStateToStorage = (traceNumber, paymentParams) => {
  try {
    const data = {
      trace_number: traceNumber,
      lastPaymentParams: paymentParams,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving trace state to localStorage:', error.message);
  }
};

/**
 * Removes all trace state (trace number, payment parameters, and PAUI
 * tombstones) from localStorage.
 */
export const clearTraceStateFromStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error(
      'Error clearing trace state from localStorage:',
      error.message,
    );
  }
};

/**
 * Extracts the PAN last-4 from an ISO/IEC 7813 Track 1 string.
 * Matches the same format validated by cb_pos's Hardware::ParseIsoIec7813 and
 * MagtekSwipeCardModal's `%B` prefix gate.
 *
 * @param {string} trackData - Full track data (Track 1 starts with `%B`)
 * @returns {string|undefined} Last 4 digits of PAN, or undefined if unparseable
 */
export const extractLastFourFromTrack = (trackData) => {
  const match = trackData?.match(/%B(\d{1,19})\^/);
  return match?.[1]?.slice(-4);
};

/**
 * Merges a single field into the stored trace state's lastPaymentParams
 * without changing the stored trace itself. No-ops if nothing is stored.
 *
 * @param {string} fieldName - The param field to set (e.g. 'lastSeenPAUI', 'pendingPAUI')
 * @param {*} value - The value to store
 */
const updateTraceStateField = (fieldName, value) => {
  try {
    const stored = getTraceStateFromStorage();
    if (!stored) return;
    saveTraceStateToStorage(stored.trace_number, {
      ...stored.lastPaymentParams,
      [fieldName]: value,
    });
  } catch (error) {
    console.error(
      `Error updating trace state field '${fieldName}':`,
      error.message,
    );
  }
};

/**
 * Records that a DPS payment attempt has concluded by storing the attempt's
 * PAUI (payment_attempt_unique_identifier) as a tombstone. The PAUI is a
 * per-attempt identifier generated by cb_pos/DPS — distinct from the
 * trace, which is the FE-generated UUID used by Ghenghis for
 * duplicate payment detection.
 *
 * When `lastSeenPAUI` is present in storage, the next call to
 * getOrCreateTraceNumber will generate a fresh trace instead of
 * reusing the stored one. Only invoked from DPS response paths — the
 * legacy path relies on card_last_four for card-swap detection.
 *
 * @param {object} params
 * @param {string} params.lastSeenPAUI - The PAUI from the concluded DPS attempt
 */
export const markResponseReceived = ({ lastSeenPAUI }) =>
  updateTraceStateField('lastSeenPAUI', lastSeenPAUI);

/**
 * Marks a DPS attempt as indeterminate (timed out without a terminal
 * response). Stores the attempt's PAUI so that makePaymentEpic can
 * resume polling for its outcome before initiating a new payment —
 * preventing duplicate charges when the timed-out attempt may still
 * be processing in DPS.
 *
 * Unlike `lastSeenPAUI` (which signals a concluded attempt and forces
 * a fresh trace), `pendingPAUI` does not affect trace generation
 * directly. It is consumed by the resume-polling logic in
 * makePaymentEpic.
 *
 * @param {object} params
 * @param {string} params.pendingPAUI - The PAUI from the timed-out DPS attempt
 */
export const markAttemptIndeterminate = ({ pendingPAUI }) =>
  updateTraceStateField('pendingPAUI', pendingPAUI);

/**
 * Returns the PAUI from a previous indeterminate (timed-out) DPS attempt,
 * or null if there is none. Used by makePaymentEpic to resume polling
 * before starting a new payment.
 *
 * @returns {string|null} The pending PAUI, or null
 */
export const getPendingPAUI = () => {
  try {
    const stored = getTraceStateFromStorage();
    return stored?.lastPaymentParams?.pendingPAUI ?? null;
  } catch (error) {
    console.error('Error reading pending PAUI:', error.message);
    return null;
  }
};

/**
 * Clears the pendingPAUI flag from stored trace params without removing
 * the trace itself. Called after the pending attempt's status is resolved.
 */
export const clearPendingPAUI = () => {
  try {
    const stored = getTraceStateFromStorage();
    if (!stored || !stored.lastPaymentParams?.pendingPAUI) return;
    const params = { ...stored.lastPaymentParams };
    delete params.pendingPAUI;
    saveTraceStateToStorage(stored.trace_number, params);
  } catch (error) {
    console.error('Error clearing pending PAUI:', error.message);
  }
};

/**
 * Compares current payment parameters with stored parameters to determine
 * if the trace number should be reused (retry scenario) or a new one should be generated
 * @param {object} currentParams - Current payment parameters
 * @param {object} storedParams - Previously stored payment parameters
 * @returns {boolean} True if trace number should be reused (parameters match)
 */
export const shouldReuseTraceNumber = (currentParams, storedParams) => {
  if (!currentParams || !storedParams) {
    return false;
  }

  // DPS tombstone: a prior DPS attempt produced a PAUI in its response, so
  // the FE has observed the outcome. This attempt is therefore new — not a
  // timeout retry. Tombstone is only written on the DPS path; on
  // the legacy path card-swap detection is handled by card_last_four below.
  if (storedParams.lastSeenPAUI) {
    return false;
  }

  // Compare key payment parameters
  const fieldsToCompare = [
    'payment_tender_type',
    'amount_given',
    'acct_no',
    'visit_id',
    'service_fee',
    'convenience_fee',
    'card_last_four',
  ];

  return fieldsToCompare.every((field) => {
    // Handle optional fields (like acct_no which may not be present)
    if (
      currentParams[field] === undefined &&
      storedParams[field] === undefined
    ) {
      return true;
    }
    return currentParams[field] === storedParams[field];
  });
};

/**
 * Gets or creates a trace number for a card payment
 * @param {object} paymentParams - Payment parameters to associate with the trace number
 * @returns {string} The trace number to use for this payment
 */
export const getOrCreateTraceNumber = (paymentParams) => {
  const stored = getTraceStateFromStorage();

  // If we have stored data and parameters match (retry scenario), reuse trace number
  if (
    stored &&
    shouldReuseTraceNumber(paymentParams, stored.lastPaymentParams)
  ) {
    return stored.trace_number;
  }

  // Otherwise, generate a new trace number
  const newTraceNumber = generateTraceNumber();
  saveTraceStateToStorage(newTraceNumber, paymentParams);
  return newTraceNumber;
};
